import React, { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useParams, useSearchParams, Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  MenuItem,
  Menu,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Avatar,
  Checkbox,
  Skeleton,
} from "@mui/material";
import sh from "bundles/common/utils/sh";
import { useSelector } from "react-redux";
import RecruiterTimelinePanel from "bundles/Internal/components/common/RecruiterTimelinePanel";
import FeatureActivityPanel from "bundles/Internal/components/common/FeatureActivityPanel";
import EngagementPanel from "bundles/Internal/components/common/EngagementPanel";
import CompanyInsightsPanel from "bundles/Internal/components/common/CompanyInsightsPanel";
import {
  ArrowLeft,
  MoreVertical,
  User,
  Mail,
  Phone,
  Building2,
  Briefcase,
  Users,
  Activity,
  X,
} from "lucide-react";
import CopyButton from "bundles/Internal/components/common/CopyButton";
import ChangePasswordDialog from "bundles/Internal/components/common/ChangePasswordDialog";
import ConfirmDialog from "bundles/Internal/components/common/ConfirmDialog";
import { enqueueSnackbar } from "notistack";
import { motion } from "framer-motion";
import {
  COLUMNS_CONSTANTS,
  SIGNUP_ORIGIN_COLOR,
  PREFERRED_LOCALE_COLOR,
} from "bundles/Internal/components/recruiter/RecruiterTable";

const DATE_KEYS = new Set(["created_at", "deleted_at"]);

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("fr-BE", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
};

const pill = (label, c) => (
  <Chip
    label={String(label).replaceAll("_", " ")}
    size="small"
    sx={{
      backgroundColor: c.bg,
      color: c.text,
      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: "8px",
      height: 24,
    }}
  />
);

const formatRecruiterCell = (recruiter, key) => {
  const value = recruiter?.[key];
  if (value === null || value === undefined || value === "") return "";

  if (key === "active" || key === "super_user" || key === "password_generated") {
    return value ? (
      <Checkbox checked disabled size="small" sx={{ color: "#079344 !important", p: 0 }} />
    ) : (
      <X size={20} color="#d32f2f" />
    );
  }

  if (DATE_KEYS.has(key)) return formatDateTime(value);

  if (key === "signup_origin") {
    return pill(value, SIGNUP_ORIGIN_COLOR[value] || SIGNUP_ORIGIN_COLOR.form);
  }
  if (key === "preferred_locale") {
    return pill(value, PREFERRED_LOCALE_COLOR[value] || PREFERRED_LOCALE_COLOR.en);
  }

  if (key === "company_name") {
    if (!value) return "";
    return recruiter?.company_id ? (
      <RouterLink
        to={`/internal/companies/${recruiter.company_id}`}
        style={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
      >
        {value}
      </RouterLink>
    ) : value;
  }

  if (key === "email" || key === "phone") {
    if (!value) return "";
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Box component="span">{value}</Box>
        <CopyButton value={value} />
      </Stack>
    );
  }

  if (key === "godview_url") {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#079344", fontFamily: "Segoe UI Semibold, sans-serif, system-ui", textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
      >
        Godview ↗
      </a>
    );
  }

  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const sectionPaperSx = {
  borderRadius: "0.75rem",
  overflow: "hidden",
  border: "1px solid hsl(140 8% 88%)",
  p: 4,
};

const sectionTitleSx = {
  color: "hsl(150 30% 12%)",
  fontWeight: 600,
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  mb: 3,
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const KeyValueTile = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: "calc(0.75rem - 2px)",
      backgroundColor: "hsl(140 10% 95%)",
      height: "100%",
    }}
  >
    <Typography
      sx={{
        color: "hsl(150 10% 46%)",
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        fontWeight: 600,
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: "hsl(150 30% 12%)",
        mt: 0.5,
        fontSize: 14,
        fontFamily: "Segoe UI Regular, sans-serif, system-ui",
      }}
    >
      {value}
    </Box>
  </Box>
);

const tableHeadCellSx = {
  backgroundColor: "transparent",
  borderBottom: "1px solid hsl(140 8% 88%)",
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 10,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "hsl(150 10% 46%)",
  py: "10px",
  px: "16px",
  whiteSpace: "nowrap",
};

const tableBodyCellSx = {
  whiteSpace: "nowrap",
  py: "14px",
  px: "16px",
  fontSize: 12,
  fontFamily: "Segoe UI Regular, sans-serif, system-ui",
  color: "hsl(150 30% 12%)",
  maxWidth: 240,
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "1px solid hsl(140 8% 88%)",
};

const OFFER_STATUS_COLOR = {
  online: { border: "#079344", bg: "rgba(7,147,68,0.10)", text: "#079344" },
  draft: { border: "#6A1B9A", bg: "rgba(106,27,154,0.10)", text: "#6A1B9A" },
  closed: { border: "#757575", bg: "rgba(117,117,117,0.10)", text: "#757575" },
  expired: { border: "#D32F2F", bg: "rgba(211,47,47,0.10)", text: "#D32F2F" },
};

const arrayToText = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return "None";
  return arr.join(", ");
};

const locationsToText = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) return "None";
  return locations
    .map((l) => {
      if (typeof l === "string") return l;
      if (l && typeof l === "object") {
        return [l.city, l.country].filter(Boolean).join(", ") || JSON.stringify(l);
      }
      return String(l);
    })
    .join(" · ");
};

function DetailsTab({ recruiter, loading }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(8)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!recruiter) return null;

  return (
    <Grid container spacing={3} alignItems="stretch">
      {COLUMNS_CONSTANTS.map((col) => {
        const value = formatRecruiterCell(recruiter, col.key);
        if (value === "" || value === null || value === undefined) return null;
        return (
          <Grid item xs={12} sm={6} key={col.key}>
            <KeyValueTile label={col.label} value={value} />
          </Grid>
        );
      })}
    </Grid>
  );
}

function JobOffersTab({ jobOffers, loading }) {
  const navigate = useNavigate();
  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (!jobOffers || jobOffers.length === 0) {
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
              No job offers was created by this recruiter.
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "Id",
              "Job title",
              "Status",
              "Categories",
              "End of mandate",
              "Applications",
              "Views",
              "Intent to apply",
              "Locations",
              "Created",
              "Job statuses",
              "Language",
              "Creator",
            ].map((label) => (
              <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {jobOffers.map((offer) => {
            const statusColor = OFFER_STATUS_COLOR[offer.offer_status] || OFFER_STATUS_COLOR.closed;
            return (
              <TableRow
                key={offer.id}
                hover
                onClick={() => navigate(`/internal/job_offers/${offer.id}`)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={tableBodyCellSx}>{offer.id}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {offer.job_title ? (
                    <RouterLink
                      to={`/internal/job_offers/${offer.id}`}
                      style={{ color: "#079344", fontWeight: 600, textDecoration: "none" }}
                      onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                      onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                    >
                      {offer.job_title}
                    </RouterLink>
                  ) : "None"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {offer.offer_status ? pill(offer.offer_status, statusColor) : "None"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{arrayToText(offer.categories)}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDate(offer.end_of_mandate) || "None"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{offer.job_applications_count ?? 0}</TableCell>
                <TableCell sx={tableBodyCellSx}>{offer.view_count ?? 0}</TableCell>
                <TableCell sx={tableBodyCellSx}>{offer.intent_apply_count ?? 0}</TableCell>
                <TableCell sx={tableBodyCellSx}>{locationsToText(offer.locations)}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDate(offer.created_at) || "None"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{arrayToText(offer.job_statuses)}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {offer.offer_language
                    ? pill(offer.offer_language, PREFERRED_LOCALE_COLOR[offer.offer_language] || PREFERRED_LOCALE_COLOR.en)
                    : "None"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {offer.recruiter
                    ? offer.recruiter.full_name || offer.recruiter.email
                    : "None"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

function CompanyTab({ companyInfo, loading }) {
  if (loading) {
    return (
      <Grid container spacing={3}>
        {[...Array(6)].map((_, idx) => (
          <Grid item xs={12} sm={6} key={idx}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="60%" />
          </Grid>
        ))}
      </Grid>
    );
  }
  if (!companyInfo) {
    return (
      <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
        No company linked.
      </Typography>
    );
  }

  const renderBool = (v) =>
    v ? (
      <Checkbox checked disabled size="small" sx={{ color: "#079344 !important", p: 0 }} />
    ) : (
      <X size={20} color="#d32f2f" />
    );

  const tiles = [
    { label: "Id", value: companyInfo.id },
    { label: "Name", value: companyInfo.name ? (
      <RouterLink
        to={`/internal/companies/${companyInfo.id}`}
        style={{ color: "#079344", fontWeight: 600, textDecoration: "none" }}
        onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
        onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
      >
        {companyInfo.name}
      </RouterLink>
    ) : "None" },
    { label: "Industry", value: companyInfo.industry || "None" },
    { label: "Country", value: companyInfo.country || "None" },
    { label: "City", value: companyInfo.city || "None" },
    { label: "Zipcode", value: companyInfo.zipcode || "None" },
    { label: "Address", value: companyInfo.address || "None" },
    { label: "Domain", value: companyInfo.domain_name || "None" },
    { label: "Region", value: companyInfo.region || "None" },
    { label: "Team size", value: companyInfo.team_size || "None" },
    { label: "Created", value: companyInfo.creation_year || "None" },
    { label: "Recruiters", value: companyInfo.recruiters_count },
    { label: "Job offers", value: companyInfo.job_offers_count },
    { label: "Job offer credits", value: companyInfo.job_offer_credits_count },
    { label: "Active licence credit", value: renderBool(companyInfo.active_licence_credit) },
    { label: "Active job offer credit", value: renderBool(companyInfo.active_job_offer_credit) },
    { label: "Resume library access", value: renderBool(companyInfo.resume_library_access) },
  ];

  const credits = companyInfo.credits || {};
  if (credits.available_job_offer_credits !== undefined) {
    tiles.push({ label: "Available JO credits", value: credits.available_job_offer_credits });
    tiles.push({ label: "Used JO credits", value: credits.used_job_offer_credits });
    tiles.push({ label: "Activated CV access", value: credits.activated_cv_access_credits });
    tiles.push({ label: "Expired CV access", value: credits.expired_cv_access_credits });
  }

  return (
    <Grid container spacing={3} alignItems="stretch">
      {tiles.map((t, idx) => (
        <Grid item xs={12} sm={6} key={`${t.label}-${idx}`}>
          <KeyValueTile label={t.label} value={t.value} />
        </Grid>
      ))}
    </Grid>
  );
}

function CandidateInterestsTab({ interests, loading }) {
  if (loading) {
    return (
      <Stack spacing={1}>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Stack>
    );
  }
  if (!interests || interests.length === 0) {
    return (
      <Typography sx={{ color: "hsl(150 10% 46%)", fontSize: 14 }}>
        No revealed candidates.
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        overflowX: "auto",
        border: "1px solid hsl(140 8% 88%)",
        borderRadius: "8px",
        backgroundColor: "#fff",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {["Id", "First name", "Last name", "Email", "Phone", "Job title", "Revealed at"].map(
              (label) => (
                <TableCell key={label} sx={tableHeadCellSx}>{label}</TableCell>
              ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {interests.map((ci) => {
            const c = ci.candidate;
            return (
              <TableRow
                key={ci.id}
                hover
                sx={{
                  "&:hover": { backgroundColor: "hsl(140 8% 90%)" },
                  "&:last-child td": { borderBottom: "none" },
                }}
              >
                <TableCell sx={tableBodyCellSx}>
                  {c?.id ? (
                    <RouterLink
                      to={`/internal/candidates/${c.id}`}
                      style={{ color: "hsl(150 38% 17%)", fontWeight: 600, textDecoration: "none" }}
                    >
                      #{c.id}
                    </RouterLink>
                  ) : (
                    "None"
                  )}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{c?.first_name || "None"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{c?.last_name || "None"}</TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {c?.email ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.email}</Box>
                      <CopyButton value={c.email} />
                    </Stack>
                  ) : "None"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>
                  {c?.phone ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{c.phone}</Box>
                      <CopyButton value={c.phone} />
                    </Stack>
                  ) : "None"}
                </TableCell>
                <TableCell sx={tableBodyCellSx}>{c?.job_title || "None"}</TableCell>
                <TableCell sx={tableBodyCellSx}>{formatDateTime(ci.revealed_at) || "None"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}



const TAB_KEYS = ["details", "activity", "job_offers", "company", "candidate_interests"];

function RecruiterShowPage() {
  const navigate = useNavigate();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { id } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const activeTab = Math.max(TAB_KEYS.indexOf(searchParams.get("tab")), 0);
  const setActiveTab = (v) => setSearchParams({ tab: TAB_KEYS[v] }, { replace: true, state: location.state });
  const goBack = () => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/internal/recruiters"));
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsRefreshing, setInsightsRefreshing] = useState(false);
  const [insightsParams, setInsightsParams] = useState({ period: "month", startDate: null, endDate: null });
  const insightsLoadedRef = useRef(false);

  const actionsOpen = Boolean(actionsAnchorEl);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    setError(null);

    sh.get(`/internal/recruiters/${id}`)
      .then((res) => {
        setRecruiter(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to load recruiter");
      });
  }, [id]);

  useEffect(() => {
    if (!id || TAB_KEYS[activeTab] !== "activity") return;
    if (insightsParams.period === "custom" && (!insightsParams.startDate || !insightsParams.endDate)) return;

    const params = { period: insightsParams.period };
    if (insightsParams.period === "custom") {
      params.start_date = insightsParams.startDate;
      params.end_date = insightsParams.endDate;
    }

    if (insightsLoadedRef.current) setInsightsRefreshing(true);
    else setInsightsLoading(true);

    sh.get(`/internal/recruiters/${id}/insights`, { params })
      .then((res) => {
        setInsights(res.data);
        insightsLoadedRef.current = true;
      })
      .catch(() => {})
      .finally(() => {
        setInsightsLoading(false);
        setInsightsRefreshing(false);
      });
  }, [id, activeTab, insightsParams]);

  const title = useMemo(() => {
    if (!recruiter) return "";
    const name = [recruiter.first_name, recruiter.last_name].filter(Boolean).join(" ");
    return name || recruiter.email || `Recruiter #${recruiter.id}`;
  }, [recruiter]);

  const getInitials = () => {
    if (!recruiter) return "?";
    const first = recruiter.first_name?.[0] || "";
    const last = recruiter.last_name?.[0] || "";
    return (first + last).toUpperCase() || (recruiter.email?.[0]?.toUpperCase() ?? "?");
  };

  const handleSetPassword = (password) => {
    if (!id) return;
    setSavingPassword(true);

    sh.post(`/internal/recruiters/${id}/set_password`, {
      password,
      authenticity_token,
    })
      .then((res) => {
        setRecruiter(res.data);
        enqueueSnackbar("Password updated", { variant: "success" });
        setPasswordDialogOpen(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        const msg = err?.response?.data?.error || "Failed to update password";
        enqueueSnackbar(msg, { variant: "error" });
      })
      .finally(() => setSavingPassword(false));
  };

  const handleDeleteRecruiter = () => {
    if (!id) return;
    if (!recruiter) return;

    setLoading(true);
    setError(null);

    sh.delete(`/internal/recruiters/${id}`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      data: {
        authenticity_token
      },
    })
      .then(() => {
        goBack();
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          window.location.href = "/internal/sign_in";
          return;
        }
        setError("Failed to delete recruiter");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 72px)",
        backgroundColor: "hsl(140 10% 95%)",
      }}
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteRecruiter}
        title="Archive recruiter?"
        description="Are you sure you want to archive this recruiter?"
        confirmLabel="Archive"
        destructive
      />
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSubmit={handleSetPassword}
        submitting={savingPassword}
        title="Change recruiter password"
        description="Sets a new password directly. The recruiter will be marked as having a generated password."
      />

      {/* Sticky Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 30,
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(16px)",
          borderBottom: "1px solid hsl(140 8% 88%)",
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ py: 1.5 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{ minWidth: 0 }}
            >
              <Button
                onClick={goBack}
                disableRipple
                sx={{
                  minWidth: 0,
                  p: 0.75,
                  color: "hsl(150 10% 46%)",
                  "&:hover": { color: "hsl(150 30% 12%)", backgroundColor: "transparent" },
                }}
              >
                <ArrowLeft size={20} />
              </Button>

              {loading ? (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Box>
                    <Skeleton width={140} height={20} />
                    <Skeleton width={200} height={14} />
                  </Box>
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      fontSize: 15,
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      backgroundColor: "hsl(150 38% 17%)",
                      color: "hsl(80 97% 72%)",
                      flexShrink: 0,
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
                      <Typography
                        sx={{
                          fontSize: { xs: 16, sm: 18 },
                          color: "hsl(150 30% 12%)",
                          fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {title}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={2}
                      sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.25 }}
                    >
                      {recruiter?.email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                          <Typography
                            component="a"
                            href={`mailto:${recruiter.email}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontSize: 12,
                              color: "hsl(150 10% 46%)",
                              textDecoration: "none",
                              "&:hover": { color: "hsl(150 30% 12%)" },
                            }}
                          >
                            <Mail size={13} />
                            {recruiter.email}
                          </Typography>
                          <CopyButton value={recruiter.email} />
                        </Box>
                      )}
                      {recruiter?.phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                          <Typography
                            component="a"
                            href={`tel:${recruiter.phone}`}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              fontSize: 12,
                              color: "hsl(150 10% 46%)",
                              textDecoration: "none",
                              "&:hover": { color: "hsl(150 30% 12%)" },
                            }}
                          >
                            <Phone size={13} />
                            {recruiter.phone}
                          </Typography>
                          <CopyButton value={recruiter.phone} />
                        </Box>
                      )}
                      {recruiter?.company_name && (
                        <Typography
                          component={recruiter.company_id ? RouterLink : "span"}
                          to={recruiter.company_id ? `/internal/companies/${recruiter.company_id}` : undefined}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                            textDecoration: "none",
                            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                            "&:hover": recruiter.company_id ? { color: "#079344" } : {},
                          }}
                        >
                          <Building2 size={13} />
                          {recruiter.company_name}
                        </Typography>
                      )}
                      {recruiter?.job_title && (
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            fontSize: 12,
                            color: "hsl(150 10% 46%)",
                          }}
                        >
                          <Briefcase size={13} />
                          {recruiter.job_title}
                        </Typography>
                      )}
                      {recruiter?.godview_url && (
                        <a
                          href={recruiter.godview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#079344", fontWeight: 600, textDecoration: "none", fontSize: 12 }}
                          onMouseOver={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseOut={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          Godview ↗
                        </a>
                      )}
                    </Stack>
                  </Box>
                </Stack>
              )}

              {recruiter?.company_metrics && (
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexShrink: 0 }}>
                  <Chip
                    label={`Credits left for the company: ${recruiter.company_metrics.available_licence ?? 0}/${recruiter.company_metrics.total_licence ?? 0}`}
                    size="small"
                    sx={{
                      backgroundColor: "rgba(25,118,210,0.10)",
                      color: "#1976D2",
                      fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                      fontSize: 11,
                      fontWeight: 600,
                      borderRadius: "8px",
                      height: 24,
                    }}
                  />
                  {recruiter.company_metrics.usage_delta_pct != null && (
                    <Chip
                      label={`Recruiter logins: ${
                        recruiter.company_metrics.usage_delta_pct >= 0
                          ? `↑${Math.abs(recruiter.company_metrics.usage_delta_pct)}%`
                          : `↓${Math.abs(recruiter.company_metrics.usage_delta_pct)}%`
                      } vs last month`}
                      size="small"
                      sx={{
                        backgroundColor:
                          recruiter.company_metrics.usage_delta_pct >= 0
                            ? "rgba(7,147,68,0.10)"
                            : "rgba(211,47,47,0.10)",
                        color:
                          recruiter.company_metrics.usage_delta_pct >= 0 ? "#079344" : "#D32F2F",
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: "8px",
                        height: 24,
                      }}
                    />
                  )}
                </Box>
              )}

              <Box sx={{ flexShrink: 0 }}>
                <Button
                  endIcon={<MoreVertical size={18} />}
                  onClick={(e) => setActionsAnchorEl(e.currentTarget)}
                  disabled={loading || !recruiter?.id}
                  disableRipple
                  size="small"
                  sx={{
                    color: "hsl(150 30% 12%)",
                    fontWeight: 600,
                    fontSize: 13,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    textTransform: "none",
                    borderRadius: "0.5rem",
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "#fff",
                    border: "1px solid hsl(140 8% 88%)",
                    transition: "opacity 0.15s ease",
                    "&:hover": { backgroundColor: "#fff", opacity: 0.8 },
                  }}
                >
                  Actions
                </Button>

                <Menu
                  disableScrollLock
                  anchorEl={actionsAnchorEl}
                  open={actionsOpen}
                  onClose={() => setActionsAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                  <MenuItem
                    onClick={() => {
                      setActionsAnchorEl(null);
                      setPasswordDialogOpen(true);
                    }}
                  >
                    Change password
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setActionsAnchorEl(null);
                      setShowDeleteConfirm(true);
                    }}
                    sx={{ color: "hsl(0 72% 51%)" }}
                  >
                    Archive recruiter
                  </MenuItem>
                </Menu>
              </Box>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Page Content */}
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} lg={["job_offers", "activity"].includes(TAB_KEYS[activeTab]) ? 12 : 8}>
            <Box sx={{ borderBottom: "1px solid hsl(140 8% 88%)", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontSize: 13,
                    minHeight: 36,
                    py: 0.5,
                    px: 1.5,
                    fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                    color: "hsl(150 10% 46%)",
                    "&.Mui-selected": { color: "hsl(150 38% 17%)" },
                  },
                  "& .MuiTab-iconWrapper": { fontSize: 16 },
                  "& .MuiTabs-indicator": { backgroundColor: "hsl(150 38% 17%)" },
                }}
              >
                <Tab icon={<User size={16} />} iconPosition="start" label="Details" />
                <Tab
                  icon={<Activity size={16} />}
                  iconPosition="start"
                  label="Activity"
                />
                <Tab
                  icon={<Briefcase size={16} />}
                  iconPosition="start"
                  label={`Job Offers${
                    recruiter?.job_offers?.length ? ` (${recruiter.job_offers.length})` : ""
                  }`}
                />
                <Tab
                  icon={<Building2 size={16} />}
                  iconPosition="start"
                  label="Company"
                />
                <Tab
                  icon={<Users size={16} />}
                  iconPosition="start"
                  label={`Candidate Interests${
                    recruiter?.candidate_interests?.length
                      ? ` (${recruiter.candidate_interests.length})`
                      : ""
                  }`}
                />
              </Tabs>
            </Box>

            <Paper elevation={0} sx={{ ...sectionPaperSx, ...(["job_offers", "activity"].includes(TAB_KEYS[activeTab]) && { p: 0 }) }}>
              {error && (
                <Box
                  sx={{
                    p: 2,
                    m: 4,
                    backgroundColor: "hsla(0, 72%, 51%, 0.08)",
                    border: "1px solid hsla(0, 72%, 51%, 0.2)",
                    borderRadius: "calc(0.75rem - 2px)",
                  }}
                >
                  <Typography sx={{ color: "hsl(0 72% 51%)", fontSize: 14 }}>{error}</Typography>
                </Box>
              )}

              {TAB_KEYS[activeTab] === "details" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <User size={20} color="hsl(150 38% 17%)" />
                    Recruiter Details
                  </Typography>
                  <DetailsTab recruiter={recruiter} loading={loading} />
                </>
              )}

              {TAB_KEYS[activeTab] === "job_offers" && (
                <JobOffersTab jobOffers={recruiter?.job_offers} loading={loading} />
              )}

              {TAB_KEYS[activeTab] === "company" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <Building2 size={20} color="hsl(150 38% 17%)" />
                    Company Info
                  </Typography>
                  <CompanyTab companyInfo={recruiter?.company_info} loading={loading} />
                </>
              )}

              {TAB_KEYS[activeTab] === "candidate_interests" && (
                <>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    <Users size={20} color="hsl(150 38% 17%)" />
                    Candidate Interests
                  </Typography>
                  <CandidateInterestsTab
                    interests={recruiter?.candidate_interests}
                    loading={loading}
                  />
                </>
              )}

              {TAB_KEYS[activeTab] === "activity" && (
                <>
                  <CompanyInsightsPanel
                    companyMetrics={insights?.company_metrics}
                    loading={insightsLoading}
                    refreshing={insightsRefreshing}
                    recruiterInsights={insights?.recruiter_insights}
                    period={insightsParams.period}
                    customRange={{ startDate: insightsParams.startDate, endDate: insightsParams.endDate }}
                    onPeriodChange={(p) => setInsightsParams((s) => ({ ...s, period: p }))}
                    onCustomRangeChange={(r) => setInsightsParams((s) => ({ ...s, startDate: r.startDate, endDate: r.endDate }))}
                    activityLog={<FeatureActivityPanel scope="recruiter" recruiterId={id} />}
                    timeOnPlatform={<EngagementPanel scope="recruiter" recruiterId={id} period={insightsParams.period} customRange={{ startDate: insightsParams.startDate, endDate: insightsParams.endDate }} />}
                  />
                </>
              )}

            </Paper>
          </Grid>

          {!["job_offers", "activity"].includes(TAB_KEYS[activeTab]) && (
            <Grid item xs={12} lg={4}>
              <Box
                sx={{
                  position: { lg: "sticky" },
                  top: { lg: 80 },
                }}
              >
                <RecruiterTimelinePanel recruiterId={id} />
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

export default RecruiterShowPage;
